import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Line } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import messages from 'components/Apps/SlaMonitor/translate'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import {
  fetchSensorsData,
  resetSensorsData,
  resetActiveSensorTypes,
  resetActiveSensorNames,
} from 'reducers/sensor/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import { DATASET_COLORS, CHART_COLORS } from 'constants/colors'
import licenseeDigitFormat from 'helpers/licenseeDigitFormat'

class SensorsChart extends Component {
  componentDidMount() {
    this.fetchSensorsData(this.props)
  }

  shouldComponentUpdate(nextProps) {
    const { activeNames } = this.props

    if (activeNames !== nextProps.activeNames || nextProps.needRefreshData) {
      this.fetchSensorsData(nextProps)

      return false
    }

    return true
  }

  componentWillUnmount() {
    const { resetActiveSensorNames, resetActiveSensorTypes } = this.props

    resetActiveSensorTypes()
    resetActiveSensorNames()
  }

  getYAxes = (position, labelString, display) => ({
    display,
    stacked: false,
    id: position,
    position,
    ticks: {},
    scaleLabel: {
      display: Boolean(labelString),
      labelString,
    },
  })

  // eslint-disable-next-line max-statements
  getChartParams = () => {
    const {
      intl: { formatMessage },
      sensorsData,
    } = this.props
    const data = sensorsData.get('datasets')
    const labels = sensorsData.get('dates')
    const xTicks = {
      beginAtZero: false,
      autoSkipPadding: 20,
    }
    const yAxes = []
    const datasets = []
    const dataLength = data.length
    let count = 0

    for (let key = 0; key < dataLength; key += 1) {
      const { name, rows, unit } = data[key]
      const dsDirection = key ? 'right' : 'left'

      for (let key2 = 0; key2 < rows.length; key2 += 1) {
        const { label, row, max_value_set } = rows[key2]
        const datasetColor = DATASET_COLORS[count]

        count = count !== DATASET_COLORS.length - 1 ? count + 1 : 0

        if (row.length === 0) {
          continue
        }

        const dataset = {
          lineTension: 0,
          label,
          fill: false,
          backgroundColor: datasetColor,
          borderColor: datasetColor,
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data: row,
        }

        if (dataLength < 3) {
          dataset.yAxisID = dsDirection
        }

        if (max_value_set !== null) {
          const maxValueDatasetData = []
          const dsMaxValueColor = CHART_COLORS[key + key2]

          for (let lKey = 0; lKey < labels.length; lKey += 1) {
            maxValueDatasetData.push(max_value_set)
          }

          datasets.push({
            type: 'line',
            label: `${label} ${formatMessage(messages.max)}`,
            fill: false,
            pointRadius: 0,
            pointHitRadius: 0,
            pointBorderWidth: 0,
            pointHoverRadius: 0,
            borderWidth: 1.5,
            borderDash: [10, 6],
            borderColor: dsMaxValueColor,
            offset: false,
            data: maxValueDatasetData,
          })
        }

        datasets.push(dataset)
      }

      if (dataLength < 3) {
        yAxes.push(
          this.getYAxes(key ? 'right' : 'left', `${name} (${unit})`, true),
        )
      }
    }

    if (!yAxes.length || dataLength > 2) {
      yAxes.push(this.getYAxes('left', '', true))
      yAxes.push(this.getYAxes('right', '', false))
    }

    if (yAxes.length === 1) {
      yAxes.push(this.getYAxes('right', '', false))
    }

    return {
      labels,
      datasets,
      yAxes,
      xTicks,
    }
  }

  chartData = ({ labels, datasets }) => ({
    labels,
    datasets,
  })

  chartOptions = ({ yAxes, xTicks }) => {
    const {
      licensee,
      intl: { formatMessage },
    } = this.props

    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: true,
        align: 'start',
        position: 'bottom',
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.date),
            },
            offset: false,
            ticks: xTicks,
            gridLines: {
              drawOnChartArea: true,
            },
          },
        ],
        yAxes,
      },
      tooltips: {
        enabled: true,
        callbacks: {
          title: (point, chart) => {
            const { datasetIndex, index } = point[0]
            const tooltipChartData = chart.datasets[datasetIndex].data[index].x

            return licenseeDigitFormat(tooltipChartData, licensee)
          },
        },
      },
      plugins: {
        datalabels: {
          anchor: 'start',
          color: 'black',
          align(context) {
            const index = context.dataIndex
            const value = context.dataset.data[index]

            return value < 10 ? 'end' : 'start'
          },
          offset: 1,
          labels: {
            value: {
              font: {
                weight: 'bold',
                size: ifWindowWidthLessThan(1280) ? 9 : 11,
              },
            },
          },
          formatter: () => '',
        },
      },
    }
  }

  buildSensorFiltersQueryParam = data => {
    const typePart = 'sensor_filters[:index][type]=:type'
    const sensorPart = 'sensor_filters[:index][sensor]=:sensor'

    return data
      .map((object, index) =>
        `${typePart}&${sensorPart}`
          .replace(/:index/g, index)
          .replace(/:type/g, object.type_id)
          .replace(/:sensor/g, object.id),
      )
      .join('&')
  }

  fetchSensorsData = props => {
    const { activeSite, activeNames, dateRange } = props
    const { startDate, endDate } = dateRange.toJS()
    const sensorNames = activeNames.toArray()
    const activeTypes = this.buildSensorFiltersQueryParam(sensorNames)

    if (activeTypes) {
      this.props.fetchSensorsData({
        site: activeSite.id,
        from: startDate.format('YYYY-MM-DD'),
        to: endDate.format('YYYY-MM-DD'),
        sensor_types: activeTypes,
      })
    } else {
      this.props.resetSensorsData()
    }
  }

  renderUnavailableDataHint = () => {
    const {
      intl: { formatMessage },
      typeList,
      activeSite,
    } = this.props
    let result = null

    if (typeList.size === 0 && !!activeSite) {
      result = (
        <div className="no-data-hint">
          {formatMessage(messages.unavailableDataMessage)}
        </div>
      )
    }

    return result
  }

  render() {
    const { sensorsChartLoader } = this.props
    const chartParams = this.getChartParams()

    return (
      <div className="chart-content">
        {this.renderUnavailableDataHint()}
        <CircleLoader loading={sensorsChartLoader} />
        <Line
          width={null}
          height={null}
          data={this.chartData(chartParams)}
          options={this.chartOptions(chartParams)}
        />
      </div>
    )
  }
}

export default connect(
  state => ({
    activeSite: state.getIn(['profile', 'profile', 'site']),
    dateRange: state.getIn(['sensor', 'dateRange']),
    typeList: state.getIn(['sensor', 'sensorTypes']),
    activeTypes: state.getIn(['sensor', 'activeTypes']),
    activeNames: state.getIn(['sensor', 'activeNames']),
    sensorsData: state.getIn(['sensor', 'sensorsData']),
    sensorsChartLoader: state.getIn(['sensor', 'sensorsChartLoader']),
    needRefreshData: state.getIn(['sensor', 'refreshSensorData']),
    licensee: state.getIn(['profile', 'profile', 'licensee']),
  }),
  {
    fetchSensorsData,
    resetSensorsData,
    resetActiveSensorTypes,
    resetActiveSensorNames,
  },
)(injectIntl(SensorsChart))
